/**
 * resolveStorageUrl — turn an API-supplied file path into a loadable URL.
 *
 * The backend returns upload URLs from whichever storage disk is configured:
 * absolute (`https://…blob.core.windows.net/uploads/avatars/x.jpg`) in
 * production, and possibly a host-relative `/storage/avatars/x.jpg` in
 * development. A host-relative path is relative to the *API* host, not the
 * app host, so the browser has to be told where to look.
 *
 * This lived inline in AuthContext while ProfilePage hand-rolled its own copy
 * that hardcoded port 8000 (`${protocol}//${hostname}:8000${path}`) — correct
 * in local dev, broken on every deployed environment where the API is not on
 * :8000. Single implementation, derived from REACT_APP_API_URL.
 */

/** Fallback API base used when REACT_APP_API_URL is not configured (local dev) */
const DEFAULT_API_URL = 'http://localhost:8000/api/v1';

/**
 * Returns the API origin with the `/api/v1` suffix stripped, i.e. the host that
 * serves `/storage/...` assets.
 */
export const getApiHost = (): string =>
	(process.env.REACT_APP_API_URL || DEFAULT_API_URL).replace(/\/api\/v\d+\/?$/, '');

/**
 * Prefix the API host onto a relative storage path. Absolute URLs and empty
 * values pass through untouched.
 *
 * @param path - Storage path or absolute URL returned by the API
 * @returns A URL the browser can load, or null when there is no path
 */
export const resolveStorageUrl = (path: string | null | undefined): string | null => {
	if (!path) return null;
	// Already absolute (Azure Blob, or APP_URL-prefixed public disk) — use as-is
	if (path.startsWith('http')) return path;
	return `${getApiHost()}${path}`;
};

export default resolveStorageUrl;
